/**
 * Server reachability, for the "we'll be right back" screen.
 *
 * The API client reports requests that failed in a way that might mean the
 * server is down (no response at all, or a 502/503/504). That alone is not
 * proof — a single call can fail, and our own API returns 502 when a
 * third-party (e.g. PostHog) times out — so ServerOutageGuard confirms with a
 * direct /health probe before showing anything.
 */

const listeners = new Set();

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const HEALTH_URL = `${API_URL.replace(/\/api\/?$/, '')}/health`;

const SUSPECT_STATUSES = [502, 503, 504];

/** Whether a failed axios request suggests the server may be unreachable. */
export function isSuspectFailure(error) {
  if (!error || error.__CANCEL__ || error.code === 'ERR_CANCELED') return false;
  if (!error.response) return true;
  return SUSPECT_STATUSES.includes(error.response.status);
}

export function reportPossibleOutage() {
  listeners.forEach((fn) => {
    try { fn(); } catch (_) { /* a listener must never break the API client */ }
  });
}

export function onPossibleOutage(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** true when /health answers OK within `timeoutMs`. */
export async function probeHealth(timeoutMs = 5000) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  try {
    const res = await fetch(HEALTH_URL, { cache: 'no-store', signal: controller?.signal });
    return !!res.ok;
  } catch (_) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
